// Lib
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Capacitor } from '@capacitor/core';

// Utils
import { getScreenOrientation } from '../utils/platformInitialisationUtils';
import logger from '../../logger';

// Selectors
import {
    getMilanoteApplicationModeSelector,
    getPlatformFormFactorSelector,
    selectScreenOrientation,
} from '../platformSelector';
import { selectIsMobileLandscapeModeAllowed } from './mobileScreenOrientationSelector';

// Actions
import { updatePlatformDetails } from '../platformActions';

// Plugins
import { DeviceInformation } from '../../../capacitor_plugins/deviceInformation';

// Constants
import {
    CAPACITOR_DEVICE_INFORMATION_PLUGIN,
    CAPACITOR_MN_KEYBOARD_PLUGIN,
} from '../../../capacitor_plugins/pluginConstants';
import { KeyboardPlugin } from '../../../capacitor_plugins/keyboard';
import virtualKeyboardHeightSingleton from '../../utils/keyboard/virtualKeyboardHeightSingleton';

type ChangeEvent = Event & {
    target: {
        type: OrientationType;
    };
};

/**
 * Determines the screen orientation to render the app in.
 */
export default function useScreenOrientationListener() {
    const appMode = useSelector(getMilanoteApplicationModeSelector);
    const formFactor = useSelector(getPlatformFormFactorSelector);
    const currentScreenOrientation = useSelector(selectScreenOrientation);
    const isLandscapeAllowed = useSelector(selectIsMobileLandscapeModeAllowed);

    const dispatch = useDispatch();

    // When the appMode or formFactor changes, recalculate the screen orientation
    useEffect(() => {
        const nextScreenOrientation = getScreenOrientation(formFactor, appMode);

        if (currentScreenOrientation === nextScreenOrientation) return;

        dispatch(updatePlatformDetails({ screenOrientation: nextScreenOrientation }));
    }, [appMode, formFactor]);

    // When the screen orientation changes, update the platform details
    useEffect(() => {
        const abortController = new AbortController();

        screen.orientation?.addEventListener(
            'change',
            (event) => {
                if ((event as ChangeEvent).target.type === currentScreenOrientation) return;

                dispatch(updatePlatformDetails({ screenOrientation: (event as ChangeEvent).target.type }));
            },
            { signal: abortController.signal },
        );

        return () => {
            abortController.abort();
        };
    }, [currentScreenOrientation]);

    useEffect(() => {
        if (!Capacitor.isPluginAvailable(CAPACITOR_MN_KEYBOARD_PLUGIN)) return;

        try {
            KeyboardPlugin.getKeyboardHeight().then(({ keyboardHeight: height }) => {
                virtualKeyboardHeightSingleton.resetHeight();
                virtualKeyboardHeightSingleton.setInitialMaxHeight(height);
            });
        } catch (error) {
            logger.log('Error resetting keyboard height');
        }
    }, [currentScreenOrientation]);

    // Update the native apps when landscape permission is set/changed
    useEffect(() => {
        if (!Capacitor.isPluginAvailable(CAPACITOR_DEVICE_INFORMATION_PLUGIN)) return;

        DeviceInformation.setDeviceAttributes({ isLandscapeAllowed });
    }, [isLandscapeAllowed]);
}
